import { ShieldAlert, AlertTriangle, ScanSearch, FileWarning } from "lucide-react";

/**
 * RegulatoryPanel — visualisiert den Branchen-Sensing-Output, den Audit-Puffer
 * (Netto-Entwicklung vs. Compliance-adjustierte Gesamtdauer) und den
 * Divergenz-Hinweis. Wird nur gerendert, wenn das Projekt als reguliert
 * eingestuft wurde.
 */
export default function RegulatoryPanel({ regulatory }) {
  if (!regulatory || !regulatory.is_regulated) {
    return null;
  }

  const {
    sector_label,
    multiplier,
    matched_keywords = [],
    baseHours_min,
    baseHours_max,
    adjustedHours_min,
    adjustedHours_max,
    deltaHours_min,
    deltaHours_max,
    divergence_warning,
    audit_label,
    audit_phase_no,
    rationale,
  } = regulatory;

  const baseDaysMin = Math.round((baseHours_min || 0) / 8);
  const baseDaysMax = Math.round((baseHours_max || 0) / 8);
  const adjDaysMin = Math.round((adjustedHours_min || 0) / 8);
  const adjDaysMax = Math.round((adjustedHours_max || 0) / 8);
  const deltaDaysMin = Math.round((deltaHours_min || 0) / 8);
  const deltaDaysMax = Math.round((deltaHours_max || 0) / 8);
  const pct = Math.round(((multiplier || 1) - 1) * 100);

  return (
    <div
      data-testid="regulatory-panel"
      className="glass-surface rounded-lg p-5 sm:p-6 border border-[#F59E0B]/30"
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-[#F59E0B]/15">
            <ShieldAlert className="w-4 h-4 text-[#F59E0B]" />
          </div>
          <div>
            <h3 className="font-['Outfit'] text-base font-semibold text-white">
              Regulatorischer Aufwand & Audit-Puffer
            </h3>
            <p className="text-[11px] text-[#606070] font-['JetBrains_Mono'] mt-0.5">
              Branchen-Sensing · Compliance-Adjustierung
            </p>
          </div>
        </div>
        <div
          data-testid="regulatory-multiplier"
          className="px-3 py-1.5 rounded-md bg-[#F59E0B]/10 border border-[#F59E0B]/40"
        >
          <div className="text-[9px] text-[#F59E0B] font-['JetBrains_Mono'] uppercase tracking-wider">
            Puffer
          </div>
          <div className="text-base font-['JetBrains_Mono'] font-bold text-[#FBBF24] leading-tight">
            ×{(multiplier || 1).toFixed(2)}
          </div>
        </div>
      </div>

      {/* Sector chip */}
      <div className="mb-4 flex items-start gap-2 flex-wrap">
        <ScanSearch className="w-4 h-4 text-[#F59E0B] mt-0.5 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div
            data-testid="regulatory-sector"
            className="text-sm font-['Outfit'] font-semibold text-white"
          >
            {sector_label}
          </div>
          {matched_keywords.length > 0 && (
            <div className="mt-1 flex flex-wrap gap-1">
              <span className="text-[10px] text-[#606070] font-['JetBrains_Mono'] mr-1">
                Erkannt an:
              </span>
              {matched_keywords.map((kw, i) => (
                <span
                  key={i}
                  className="text-[10px] px-1.5 py-0.5 rounded bg-[#F59E0B]/10 text-[#FBBF24] font-['JetBrains_Mono']"
                >
                  {kw}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Hours comparison grid */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
        <div className="rounded-md p-3 bg-white/[0.02] border border-white/10">
          <div className="text-[10px] text-[#606070] font-['JetBrains_Mono'] uppercase tracking-wider">
            Netto-Entwicklung
          </div>
          <div className="mt-1 text-sm font-['JetBrains_Mono'] text-white font-semibold">
            {baseHours_min}–{baseHours_max} h
          </div>
          <div className="text-[10px] text-[#A0A0B0] font-['JetBrains_Mono'] mt-0.5">
            ≈ {baseDaysMin}–{baseDaysMax} T.
          </div>
        </div>
        <div className="rounded-md p-3 bg-[#F59E0B]/10 border border-[#F59E0B]/40 ring-1 ring-[#F59E0B]/20">
          <div className="text-[10px] text-[#FBBF24] font-['JetBrains_Mono'] uppercase tracking-wider">
            Compliance-Adjusted
          </div>
          <div
            data-testid="regulatory-adjusted-hours"
            className="mt-1 text-sm font-['JetBrains_Mono'] text-white font-bold"
          >
            {adjustedHours_min}–{adjustedHours_max} h
          </div>
          <div className="text-[10px] text-[#FBBF24] font-['JetBrains_Mono'] mt-0.5">
            ≈ {adjDaysMin}–{adjDaysMax} T.
          </div>
        </div>
        <div className="rounded-md p-3 bg-white/[0.02] border border-white/10">
          <div className="text-[10px] text-[#606070] font-['JetBrains_Mono'] uppercase tracking-wider">
            Differenz
          </div>
          <div
            data-testid="regulatory-delta-hours"
            className="mt-1 text-sm font-['JetBrains_Mono'] text-[#FCA5A5] font-semibold"
          >
            +{deltaHours_min}–{deltaHours_max} h
          </div>
          <div className="text-[10px] text-[#FCA5A5] font-['JetBrains_Mono'] mt-0.5">
            +{deltaDaysMin}–{deltaDaysMax} T. ({pct}%)
          </div>
        </div>
      </div>

      {/* Rationale */}
      {rationale && (
        <div className="mb-3 text-[11px] text-[#A0A0B0] font-['Inter'] leading-relaxed">
          <span className="text-[#FBBF24] font-semibold">Begründung:</span>{" "}
          {rationale}
        </div>
      )}

      {/* Divergence warning */}
      {divergence_warning && (
        <div
          data-testid="regulatory-divergence-warning"
          className="flex items-start gap-2 p-3 rounded-md bg-[#7F1D1D]/30 border border-[#EF4444]/40 mb-3"
        >
          <AlertTriangle className="w-4 h-4 text-[#FCA5A5] flex-shrink-0 mt-0.5" />
          <div className="text-[11px] text-[#FECACA] font-['Inter'] leading-relaxed">
            {divergence_warning}
          </div>
        </div>
      )}

      {/* Critical-path link */}
      {audit_phase_no && audit_label && (
        <div className="flex items-center gap-2 text-[11px] font-['JetBrains_Mono'] text-[#A0A0B0]">
          <FileWarning className="w-3.5 h-3.5 text-[#EF4444]" />
          Phase&nbsp;
          <span className="text-[#FCA5A5] font-bold">{audit_phase_no}</span>
          &nbsp;—&nbsp;
          <span className="text-white">{audit_label}</span>
          &nbsp;ist als&nbsp;
          <span className="text-[#EF4444] font-bold">kritischer Pfad</span>
          &nbsp;markiert.
        </div>
      )}
    </div>
  );
}
